<template>
  <v-dialog v-model="dialog" width="800" :persistent="true">
    <template v-slot:activator="{ on }">
      <v-btn color="success" @click="open">
        {{ 'app.add' | trans }}
      </v-btn>
    </template>
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close()">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container grid-list-md no-padding>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-layout row wrap>
              <v-flex xs12>
                <education-picture @addImage="addImage" :url="education.image" />
              </v-flex>
              <v-flex xs12>
                <div class="v-messages theme--light error--text" v-if="imageError !== null">
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      {{ imageError }}
                    </div>
                  </div>
                </div>
              </v-flex>
              <v-flex xs12>
                <profile-image-upload
                  langType="pl"
                  method="POST"
                  field="image"
                  v-model="showAddPicture"
                  width="900"
                  height="400"
                  :lang-ext="langExt"
                  :url="routes.upload_image"
                  :headers="getHeaders()"
                  @crop-upload-success="cropUploadSuccess"
                />
              </v-flex>
              <v-flex xs12>
                <v-text-field v-model="education.tutor" :label="'app.education.tutor' | trans" :rules="rules.tutor" />
              </v-flex>
              <v-flex xs12>
                <v-select
                  v-model="education.video_name"
                  :items="availableVideos"
                  :label="'app.education.video' | trans"
                  :rules="rules.video"
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <v-select
                  v-model="education.type"
                  :items="availableTypes"
                  :label="'app.education.type' | trans"
                  :rules="rules.type"
                  item-text="label"
                  item-value="value"
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="education.name"
                  :label="'app.education.name' | trans"
                  :rules="rules.name"
                />
              </v-flex>
              <v-flex xs12>
                <v-select
                  :items="translatedRoles"
                  :label="'app.pages.roles' | trans"
                  :multiple="true"
                  :rules="rules.roles"
                  :disabled="education.type === instructionalMaterialsType"
                  v-model="education.roles"
                />
              </v-flex>
              <v-flex xs12>
                <v-textarea
                  v-model="education.shortDescription"
                  :label="'app.education.short_description' | trans"
                  :no-resize="true"
                  :rules="rules.shortDescription"
                  full
                  rows="3"
                ></v-textarea>
              </v-flex>
              <v-flex xs12>
                <v-textarea
                  v-model="education.description"
                  :label="'app.education.description' | trans"
                  :no-resize="true"
                  :rules="rules.description"
                  full
                  rows="3"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <v-btn color="primary" @click="save()" :disabled="!valid" :loading="loading" ref="saveButton">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import RulesBuilder from '../../../utils/rulesBuilder';
import { trans } from '../../../app';
import profileImageUpload from 'vue-image-crop-upload';
import EducationPicture from './education-picture.vue';
import axios from 'axios';
import { mapActions } from 'vuex';
import { availableTypes as availableTypesLibs } from './libs';
import { EDUCATION_TYPE, INSTRUCTIONAL_MATERIALS_TYPE } from '../../education/libs';

export default {
  components: {
    'profile-image-upload': profileImageUpload,
    'education-picture': EducationPicture,
  },
  props: {
    routes: Object,
    videos: Array,
    roles: Array,
  },
  created() {
    const translatedRoles = this.roles.map((role) => ({
      text: trans(`page.roles.${role}`),
      value: role,
    }));
    translatedRoles.sort((r1, r2) => r1.text.localeCompare(r2.text));
    this.translatedRoles = translatedRoles;
  },
  data: function () {
    return {
      instructionalMaterialsType: INSTRUCTIONAL_MATERIALS_TYPE,
      availableTypes: availableTypesLibs(),
      translatedRoles: [],
      dialog: false,
      showAddPicture: false,
      loading: false,
      valid: false,
      rulesBuilder: new RulesBuilder(),
      imageError: null,
      education: {
        id: null,
        tutor: '',
        image: null,
        video_name: '',
        name: '',
        shortDescription: '',
        description: '',
        roles: [],
        type: '',
      },
      langExt: {
        hint: trans('app.settings.drag_and_drop'),
        loading: trans('app.uploading'),
        success: trans('app.education.image_change_success'),
        fail: trans('app.education.image_change_failure'),
        preview: trans('app.preview'),
        btn: {
          off: trans('app.cancel'),
          close: trans('app.close'),
          back: trans('app.back'),
          save: trans('app.save'),
        },
        error: {
          onlyImg: trans('app.settings.wrong_avatar_extension'),
          lowestPx: trans('app.settings.wrong_avatar_resolution'),
        },
      },
    };
  },
  methods: {
    ...mapActions(['fetchEducation']),
    open() {
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.$refs.form.reset();
      this.imageError = null;
      this.loading = false;
      this.education.id = null;
      this.education.image = null;
    },
    addImage() {
      this.showAddPicture = true;
    },
    cropUploadSuccess(response) {
      this.education.image = response.image;
    },
    validate() {
      this.imageError = this.education.image === null ? 'Obrazek jest wymagany' : null;

      return this.$refs.form.validate() && this.imageError === null;
    },
    async save() {
      if (this.validate() === false) {
        return;
      }

      this.loading = true;
      const data = {
        tutor: this.education.tutor,
        image: this.education.image,
        video_name: this.education.video_name,
        name: this.education.name,
        short_description: this.education.shortDescription,
        description: this.education.description,
        roles: this.education.roles,
        type: this.education.type,
      };
      try {
        if (this.education.id === null) {
          await axios.post('/administrator/education', data);
        } else {
          await axios.put(`/administrator/education/${this.education.id}`, data);
        }
        this.$toasted.global.success({ message: trans('app.saved') });
        this.fetchEducation();
      } catch (e) {
        console.error({ e });
        this.$toasted.global.error({ message: trans('app.save_failed') });
      }
      this.close();
    },
    getHeaders() {
      return {
        'X-CSRF-TOKEN': document.head.querySelector('meta[name="csrf-token"]').content,
      };
    },
    openAndEdit(education) {
      this.education.id = education.id;
      this.education.tutor = education.tutor;
      this.education.image = education.image;
      this.education.video_name = education.video;
      this.education.name = education.name;
      this.education.description = education.description;
      this.education.shortDescription = education.short_description;
      this.education.roles = education.roles;
      this.education.type = education.type;
      this.open();
    },
  },
  computed: {
    title() {
      if (this.education.id === null) {
        return trans('app.add');
      }

      return trans('app.edit');
    },
    availableVideos() {
      return [...this.videos, ...(this.education.id !== null ? [this.education.video_name] : [])];
    },
    rules: function () {
      return {
        tutor: this.rulesBuilder
          .addRequiredRule(trans('app.education.tutor'))
          .addMinLengthRule(trans('app.education.tutor'), 1)
          .addMaxLengthRule(trans('app.education.tutor'), 255)
          .getRules(),
        video: this.rulesBuilder.addRequiredRule(trans('app.education.video')).getRules(),
        type: this.rulesBuilder.addRequiredRule(trans('app.education.type')).getRules(),
        name: this.rulesBuilder
          .addRequiredRule(trans('app.education.name'))
          .addMinLengthRule(trans('app.education.name'), 1)
          .addMaxLengthRule(trans('app.education.name'), 255)
          .getRules(),
        shortDescription: this.rulesBuilder
          .addRequiredRule(trans('app.education.short_description'))
          .addMinLengthRule(trans('app.education.short_description'), 1)
          .addMaxLengthRule(trans('app.education.short_description'), 5000)
          .getRules(),
        description: this.rulesBuilder
          .addRequiredRule(trans('app.education.description'))
          .addMinLengthRule(trans('app.education.description'), 1)
          .addMaxLengthRule(trans('app.education.description'), 5000)
          .getRules(),
        roles: [
          ...this.rulesBuilder.addRequiredRule(trans('app.pages.roles')).getRules(),
          ...(this.education.type === EDUCATION_TYPE
            ? this.rulesBuilder.addMinLengthRule(trans('app.pages.roles'), 1, true).getRules()
            : []),
        ],
      };
    },
  },
  watch: {
    'education.image': function (value) {
      if (value !== null) {
        this.imageError = null;
      }
    },
    'education.type': function (type) {
      if (type === INSTRUCTIONAL_MATERIALS_TYPE) {
        this.education.roles = [];
      }
    },
  },
};
</script>

<style scoped></style>
